import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Leaf, GlobeHemisphereEast, ChartLineUp, Microphone, Flower, Tree,
  Sun, Snowflake, ThermometerHot, Drop, Compass, GraduationCap, ArrowRight,
  Sparkle, Plant, Cow, Lightning,
} from "@phosphor-icons/react";
import { ReopenCookieLink } from "@/components/CookieConsent";
import { fetchLandingSpotlight } from "@/lib/api";
import LiveTicker from "@/components/landing/LiveTicker";
import MobilePulseBar from "@/components/landing/MobilePulseBar";
import PulseDashboard, { fetchPulse } from "@/components/landing/PulseDashboard";
import HowItWorks from "@/components/landing/HowItWorks";
import WhoIsItFor from "@/components/landing/WhoIsItFor";

/* ------------------------------------------------------------------------
 * Hero imagery — AI-generated drone-style WebP photos of European farmland,
 * one set per season at 480 / 960 / 1920 widths. Browser picks the smallest
 * file that fits the viewport (saves up to 80 % on mobile vs. desktop).
 * Generated via Gemini Nano Banana
 * (`scripts/generate_seasonal_hero.py` + monthly cron in `services/scheduler`).
 * ------------------------------------------------------------------------ */
const HERO_SEASONS = ["winter", "spring", "summer", "autumn"];

function heroSrcSet(season) {
  return [
    `/hero/hero-${season}-480.webp 480w`,
    `/hero/hero-${season}-960.webp 960w`,
    `/hero/hero-${season}-1920.webp 1920w`,
  ].join(", ");
}

function heroFallback(season) {
  return `/hero/hero-${season}-1920.webp`;
}

const SEASONS = {
  winter: {
    label: "Winter",
    Icon: Snowflake,
    color: "#7AA8C7",
    headline: "Die Felder ruhen — der Boden arbeitet.",
    body: "Frost zerbröselt Schollen, Mikroorganismen verschieben Nährstoffe. Was unsichtbar passiert, sehen wir auf dem Satelliten als feinen NDVI-Knick.",
  },
  spring: {
    label: "Frühling",
    Icon: Plant,
    color: "#7EE8B0",
    headline: "Alles wacht auf — gleichzeitig und überall.",
    body: "Die ersten Blüten, der Saisonstart, Bienenflug. Jetzt entscheidet jeder warme Tag über die spätere Ernte. Wir verfolgen die Welle, wie sie über DE/AT zieht.",
  },
  summer: {
    label: "Sommer",
    Icon: Sun,
    color: "#FFC24B",
    headline: "Wachstum auf Hochtouren — Stress lauert.",
    body: "Hitzewellen, Trockenstress, plötzliche Gewitter. Im Sommer wechseln die Bestände binnen einer Woche von vital zu gestresst — wir liefern dir die Frühwarnung.",
  },
  autumn: {
    label: "Herbst",
    Icon: Plant,
    color: "#A65A2D",
    headline: "Ernte, Bilanz, Ausblick.",
    body: "Was hat das Jahr gebracht? Wir vergleichen NDVI mit 60-Jahre-Klimaarchiv und zeigen dir, wie typisch oder außergewöhnlich diese Saison war.",
  },
};

const TICKER_FRAMES = [
  { icon: ThermometerHot, text: "Frostgefahr morgen früh in Mühlviertel" },
  { icon: Plant, text: "Weizen +0.08 NDVI — die Niederschläge wirken" },
  { icon: Flower, text: "Bienenflug: Salweide blüht jetzt in OÖ" },
  { icon: ChartLineUp, text: "Raps EUR/t auf 6-Wochen-Hoch" },
  { icon: Drop, text: "Bodenfeuchte in NRW unter 30 % — Bewässerung planen" },
];

const DISCOVERY = [
  { to: "/dashboard", label: "Acker", Icon: Plant, color: "#9BB23C", desc: "Vitalität · Stress · Ertrag" },
  { to: "/satellite", label: "Wald", Icon: Tree, color: "#00FA9A", desc: "NDVI · NDMI · Borkenkäfer-Risiko" },
  { to: "/bees", label: "Bienen", Icon: Flower, color: "#FFC24B", desc: "Blüh-Kalender · Nektar-Stärke" },
  { to: "/commodities", label: "Markt", Icon: ChartLineUp, color: "#FF5500", desc: "Weizen · Mais · Soja · Raps" },
  { to: "/heritage", label: "Klima", Icon: ThermometerHot, color: "#FFC24B", desc: "60-Jahre-Vergleich · Was würde Opa sagen?" },
  { to: "/markt-akademie", label: "Lernen", Icon: GraduationCap, color: "#3D7A8C", desc: "Marktverständnis · für jeden" },
];

const STORIES = [
  {
    icon: Tree,
    color: "#00FA9A",
    eyebrow: "In den Wäldern um Gmunden",
    headline: "Die Vitalität liegt 4 % über Vorjahres-Niveau.",
    body: "Was der Wald-NDVI verrät: Der milde Winter und die feuchten März-Wochen haben den Trieben einen Vorsprung gegeben. Auf der Heritage-Seite findest du den Vergleich zum Bauern-Jahr 1965.",
    cta: { to: "/heritage", label: "Heritage öffnen" },
  },
  {
    icon: Plant,
    color: "#FFC24B",
    eyebrow: "Auf den Roggenfeldern bei Wels",
    headline: "Erste Schosser-Phase — kritische 14 Tage.",
    body: "BBCH-31 startet. Jetzt entscheidet Stickstoff über die spätere Ähre. Unser Krankheitswarner zeigt, ob Mehltau-Druck im 7-Tage-Wetter steigt.",
    cta: { to: "/diseases", label: "Krankheitswarner ansehen" },
  },
  {
    icon: Cow,
    color: "#7EE8B0",
    eyebrow: "Auf den Almen im Mostviertel",
    headline: "Fruktan-Risiko Stufe 1 — entspannt anweiden.",
    body: "Kühle Nächte halten den Zucker im Gras niedrig. Pferde und Rinder können raus. Wir tracken das täglich pro Region — und für deine eigene Koppel.",
    cta: { to: "/grassland", label: "Weide-Analyse" },
  },
];

const EXPLAIN = [
  {
    icon: GlobeHemisphereEast,
    color: "#00FA9A",
    title: "Satellit & Wetter — wie geht das?",
    what: "Sentinel-2 fotografiert alle 3–5 Tage jedes Feld in Mitteleuropa. Open-Meteo speist 13 Wettermodelle ein.",
    why: "Du siehst den Bestand aus der Vogelperspektive — ohne hinzufahren. Frost, Trockenheit, Krankheits-Druck: alles sichtbar.",
    example: "Beispiel: NDVI 0.72 = sattgrün. Fällt er innerhalb einer Woche auf 0.58, ist Stress unterwegs.",
  },
  {
    icon: Leaf,
    color: "#7EE8B0",
    title: "Klartext statt Fachjargon",
    what: 'Jede Zahl bekommt eine Klartext-Übersetzung („Vital", „Mäßig", „Stress").',
    why: "Du sollst keine Doktorarbeit lesen. Du willst wissen: Ist's gut? Ist's schlecht? Was tun?",
    example: 'Beispiel: „NDMI 0.41" → „Bodenfeuchte erhöht — keine Bewässerung nötig".',
  },
  {
    icon: ChartLineUp,
    color: "#FF5500",
    title: "Markt — wo Felder auf Preise treffen",
    what: "CBOT-, MATIF- und lokale AMA/AMI-Daten in einer Ansicht.",
    why: "Wenn die Felder in Russland trocknen, bewegen sich die Weizenpreise in Linz. Wir zeigen die Verbindung.",
    example: "Beispiel: Trockenheit + Lager-Engpass = Erfasser-Preis +12 €/t binnen 7 Tagen.",
  },
];

const TRUST = [
  { value: "100%", label: "Öffentliche Daten" },
  { value: "0 €", label: "Für alle frei" },
  { value: "0", label: "Tracking-Cookies" },
  { value: "DE/AT", label: "42 Regionen abgedeckt" },
];

/* ------------------------------------------------------------------------
 * Components
 * ------------------------------------------------------------------------ */
function HeroTicker({ frames }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % frames.length), 3500);
    return () => clearInterval(t);
  }, [frames.length]);
  const Frame = frames[idx];
  const Icon = Frame.icon;
  return (
    <div
      className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 text-sm text-white"
      data-testid="hero-ticker"
    >
      <Icon size={16} weight="fill" className="text-[#C8E6C9] shrink-0" />
      <span className="font-mono text-xs">{Frame.text}</span>
    </div>
  );
}

function HeroBackground({ season }) {
  return (
    <>
      {/* Actual responsive WebP image — browser picks smallest size that fits. */}
      <img
        src={heroFallback(season)}
        srcSet={heroSrcSet(season)}
        sizes="100vw"
        alt=""
        decoding="async"
        fetchPriority="high"
        className="absolute inset-0 w-full h-full object-cover hero-kenburns"
        aria-hidden="true"
        data-testid={`hero-image-${season}`}
      />
      {/* Dark gradient overlay for text legibility */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(8,8,8,0.6), rgba(10,10,10,0.92))",
        }}
        aria-hidden="true"
      />
    </>
  );
}

function CountUp({ value, suffix = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const target = typeof value === "number" ? value : 0;
    if (typeof value !== "number") { setN(value); return; }
    let raf;
    const start = performance.now();
    const dur = 1200;
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      setN(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span>{n}{suffix}</span>;
}

function SpotlightStrip({ data }) {
  if (!data) return null;
  const { top_stress, top_vital, latest_podcast, latest_daily } = data;
  const dailyTop = (latest_daily || [])[0];
  return (
    <section
      className="relative -mt-8 z-20 max-w-6xl mx-auto px-6"
      data-testid="spotlight-strip"
    >
      <div className="bg-[#121212] border border-white/10 rounded-sm shadow-xl p-3 md:p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1 md:col-span-1">
          <Lightning size={12} weight="fill" className="text-[#FFC24B]" />
          Heute auf Bodenblick
        </div>
        {dailyTop && (
          <Link
            to="/heute"
            data-testid="spotlight-daily"
            className="block group flex items-center gap-2 md:col-span-2"
          >
            {dailyTop.image_url && (
              <img
                src={dailyTop.image_url}
                alt=""
                className="w-12 h-12 object-cover rounded-sm shrink-0"
                loading="lazy"
              />
            )}
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#7EE8B0]">
                {dailyTop.scientific ? "Wissenschaft" : (dailyTop.topic_label || "Beitrag")}
              </div>
              <div className="text-sm font-medium group-hover:text-[#00FA9A] truncate">
                {dailyTop.title}
              </div>
              <div className="text-xs text-zinc-400 truncate">{dailyTop.teaser}</div>
            </div>
          </Link>
        )}
        {top_stress && (
          <Link
            to={`/dashboard`}
            data-testid="spotlight-stress"
            className="block group min-w-0"
          >
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#FF7A45]">⚠ Aufmerksamkeit</div>
            <div className="text-sm font-medium group-hover:text-[#00FA9A] truncate">{top_stress.label}</div>
            <div className="text-xs text-zinc-400">NDVI {top_stress.ndvi?.toFixed?.(2)} · {top_stress.crop}</div>
          </Link>
        )}
        {latest_podcast ? (
          <Link
            to="/podcast"
            data-testid="spotlight-podcast"
            className="block group min-w-0"
          >
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#A99AFF] flex items-center gap-1">
              <Microphone size={10} weight="fill" /> Neueste Folge
            </div>
            <div className="text-sm font-medium group-hover:text-[#00FA9A] truncate">{latest_podcast.title}</div>
            <div className="text-xs text-zinc-400">{latest_podcast.ai_generated ? "KI · NotebookLM" : "Redaktion"}</div>
          </Link>
        ) : top_vital && (
          <Link
            to={`/dashboard`}
            data-testid="spotlight-vital"
            className="block group min-w-0"
          >
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#00FA9A]">✓ Lichtblick</div>
            <div className="text-sm font-medium group-hover:text-[#00FA9A] truncate">{top_vital.label}</div>
            <div className="text-xs text-zinc-400">NDVI {top_vital.ndvi?.toFixed?.(2)} · {top_vital.crop}</div>
          </Link>
        )}
      </div>
    </section>
  );
}

function SeasonBlock({ season }) {
  const s = SEASONS[season] || SEASONS.spring;
  const Icon = s.Icon;
  return (
    <section
      className="eid-card p-6 md:p-10 relative overflow-hidden"
      data-testid="season-block"
      style={{
        background: `linear-gradient(135deg, ${s.color}15 0%, #141414 60%, ${s.color}25 100%)`,
        borderLeft: `3px solid ${s.color}`,
      }}
    >
      <div
        className="absolute -right-12 -top-12 opacity-[0.07]"
        style={{ color: s.color }}
        aria-hidden="true"
      >
        <Icon size={280} weight="fill" />
      </div>
      <div className="relative">
        <div
          className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm"
          style={{ background: s.color, color: "white" }}
        >
          <Icon size={11} weight="fill" /> Gerade ist {s.label}
        </div>
        <h2 className="mt-3 text-2xl md:text-3xl font-medium tracking-tight max-w-2xl">
          {s.headline}
        </h2>
        <p className="mt-3 text-sm md:text-base text-white/80 max-w-2xl leading-relaxed">
          {s.body}
        </p>
      </div>
    </section>
  );
}

function StoryRow({ story, index }) {
  const Icon = story.icon;
  const reversed = index % 2 === 1;
  return (
    <article
      className={`grid grid-cols-1 md:grid-cols-12 gap-4 items-start ${reversed ? "md:[direction:rtl]" : ""}`}
      data-testid={`story-${index}`}
      style={{ direction: "ltr" }}
    >
      <div className="md:col-span-1 flex md:justify-center">
        <span
          className="p-3 rounded-sm shrink-0"
          style={{ background: `${story.color}1A`, color: story.color }}
        >
          <Icon size={22} weight="fill" />
        </span>
      </div>
      <div className="md:col-span-11">
        <div className="eid-label" style={{ color: story.color }}>{story.eyebrow}</div>
        <h3 className="text-xl md:text-2xl font-medium tracking-tight mt-1">
          {story.headline}
        </h3>
        <p className="text-sm md:text-base text-zinc-400 mt-2 leading-relaxed max-w-2xl">
          {story.body}
        </p>
        <Link
          to={story.cta.to}
          className="inline-flex items-center gap-1 text-sm mt-3 font-medium hover:underline"
          style={{ color: story.color }}
          data-testid={`story-cta-${index}`}
        >
          {story.cta.label} <ArrowRight size={12} />
        </Link>
      </div>
    </article>
  );
}

function DiscoveryGrid() {
  return (
    <section data-testid="discovery-grid">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="eid-label">Wähl deinen Einstieg</div>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight mt-1">
            Worüber willst du heute mehr wissen?
          </h2>
        </div>
        <div className="text-xs text-zinc-400 inline-flex items-center gap-1">
          <Compass size={12} /> 6 Welten — alle frei zugänglich
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {DISCOVERY.map((d) => {
          const Icon = d.Icon;
          return (
            <Link
              key={d.to}
              to={d.to}
              data-testid={`discovery-${d.label.toLowerCase()}`}
              className="eid-card p-5 group relative overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5"
              style={{ borderTop: `3px solid ${d.color}` }}
            >
              <div
                className="absolute -right-6 -bottom-6 opacity-[0.08] group-hover:opacity-[0.15] transition-opacity"
                style={{ color: d.color }}
                aria-hidden="true"
              >
                <Icon size={140} weight="fill" />
              </div>
              <div className="relative">
                <span
                  className="inline-flex p-2 rounded-sm"
                  style={{ background: `${d.color}1A`, color: d.color }}
                >
                  <Icon size={20} weight="fill" />
                </span>
                <h3 className="text-xl font-medium mt-3">{d.label}</h3>
                <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{d.desc}</p>
                <span
                  className="inline-flex items-center gap-1 text-xs mt-3 font-medium"
                  style={{ color: d.color }}
                >
                  Erkunden <ArrowRight size={11} />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function ExplainGrid() {
  return (
    <section data-testid="explain-grid">
      <div className="mb-4">
        <div className="eid-label">Was siehst du da überhaupt?</div>
        <h2 className="text-2xl md:text-3xl font-medium tracking-tight mt-1 max-w-2xl">
          Drei Antworten, ohne Doktorarbeit.
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {EXPLAIN.map((e, i) => {
          const Icon = e.icon;
          return (
            <div key={i} className="eid-card p-5 flex flex-col" data-testid={`explain-${i}`}>
              <span
                className="inline-flex p-2 rounded-sm self-start"
                style={{ background: `${e.color}1A`, color: e.color }}
              >
                <Icon size={18} weight="fill" />
              </span>
              <h3 className="text-lg font-medium tracking-tight mt-3">{e.title}</h3>
              <div className="mt-3 space-y-2 text-sm leading-relaxed">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Was ist das</div>
                  <div className="text-white">{e.what}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Warum es zählt</div>
                  <div className="text-white">{e.why}</div>
                </div>
                <div className="pt-2 border-t border-white/10">
                  <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: e.color }}>
                    Beispiel
                  </div>
                  <div className="text-zinc-400">{e.example}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TrustFooter() {
  return (
    <section
      className="eid-card p-6 md:p-8"
      data-testid="trust-footer"
      style={{ background: "linear-gradient(135deg, #0F0F0F 0%, #0E1A14 100%)" }}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkle size={16} className="text-[#00FA9A]" weight="fill" />
        <div className="eid-label">Warum Bodenblick anders ist</div>
      </div>
      <h2 className="text-xl md:text-2xl font-medium tracking-tight mt-2 max-w-2xl">
        Frei. Öffentlich. Ohne Bezahlschranke. Ohne Tracking.
      </h2>
      <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
        Wir nutzen ausschließlich öffentlich finanzierte Datenquellen — Copernicus
        Sentinel-2/3, Open-Meteo, ERA5-Reanalyse. Daraus bauen wir verständliche
        Lagebilder. Keine Algorithmen, die dich profilieren. Keine Werbung.
        Keine versteckten Premium-Features.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        {TRUST.map((t) => (
          <div
            key={t.label}
            className="bg-[#121212] border border-white/10 rounded-sm p-4 text-center"
            data-testid={`trust-${t.label.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <div className="text-2xl md:text-3xl font-medium" style={{ color: "#00FA9A" }}>{t.value}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mt-1">{t.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------
 * Main
 * ------------------------------------------------------------------------ */
export default function Landing() {
  const [spotlight, setSpotlight] = useState(null);
  const [pulse, setPulse] = useState(null);

  useEffect(() => {
    fetchLandingSpotlight().then(setSpotlight).catch(() => setSpotlight(null));
    fetchPulse().then(setPulse).catch(() => setPulse(null));
    const id = setInterval(() => {
      fetchPulse().then(setPulse).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const season = HERO_SEASONS.includes(spotlight?.season) ? spotlight.season : "spring";
  const stats = spotlight?.stats || { regions: 42, days: 30, satellites: 2 };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative z-10" data-testid="landing-root">
      <Helmet>
        <title>Bodenblick · Dein Boden. Dein Blick. Aktuell.</title>
        <meta
          name="description"
          content="Bodenblick — frei zugängliche Wissensplattform rund um Satellitendaten, Wetter und Agrarmärkte für DE / AT. Ohne Anmeldung, ohne Tracking, ohne Werbung."
        />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A]">
              <Leaf size={18} />
            </span>
            <div>
              <div className="eid-label">Bodenblick</div>
              <div className="text-sm font-medium">bodenblick.com</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/markt-akademie"
              className="text-xs text-zinc-400 hover:text-white hidden sm:inline"
              data-testid="header-academy-link"
            >
              Markt-Akademie
            </Link>
            <Link
              to="/dashboard"
              data-testid="landing-explore-link"
              className="text-xs bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-full px-4 py-1.5 inline-flex items-center gap-1"
            >
              Erkunden <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      </header>

      {/* Live Pulse — ticker + bento dashboard. Stays "above the fold" so
          visitors see real-time signals before the cinematic hero. */}
      <LiveTicker pulse={pulse} />
      <section id="pulse-section" className="bg-[#161616] py-10 md:py-14 scroll-mt-16" data-testid="pulse-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <PulseDashboard />
        </div>
      </section>

      {/* Cinematic Hero */}
      <section className="relative" data-testid="landing-hero">
        <div className="h-[88vh] sm:h-[80vh] md:h-[88vh] min-h-[640px] sm:min-h-[520px] relative overflow-hidden">
          <HeroBackground season={season} />
          <div className="absolute inset-0 flex items-end pb-32 sm:pb-24 md:pb-32">
            <div className="max-w-6xl mx-auto w-full px-6 text-white">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                <div>
                  <div className="eid-label" style={{ color: "#C8E6C9" }}>
                    Frei zugänglich · ohne Anmeldung · ohne Werbung
                  </div>
                  <h1
                    className="font-display text-[2.75rem] sm:text-5xl md:text-7xl lg:text-[7.5rem] font-bold tracking-tighter mt-3 max-w-5xl"
                    style={{ color: "white", lineHeight: 0.92 }}
                  >
                    Dein Boden.
                    <br />
                    Dein Blick.
                    <br />
                    <span style={{ color: "#C8E6C9" }}>Aktuell.</span>
                  </h1>
                  <p className="mt-5 max-w-xl text-sm sm:text-base md:text-lg" style={{ color: "#D4D4D8" }}>
                    Vom Satelliten direkt aufs Smartphone — Vitalität, Wetter und
                    Marktbewegungen für 42 Regionen in Deutschland und Österreich.
                    In Klartext. Ohne Bezahlschranke. Damit jeder verstehen kann,
                    wie es unseren Feldern geht.
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
                    <Link
                      to="/dashboard"
                      data-testid="hero-cta-explore"
                      className="bg-[#00FA9A] hover:bg-[#33FBAE] text-black rounded-full px-6 py-3 text-sm md:text-base font-semibold inline-flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(0,250,154,0.35)] w-full sm:w-auto"
                    >
                      Jetzt Erkunden <ArrowRight size={14} weight="bold" />
                    </Link>
                    <Link
                      to="/entdecken"
                      data-testid="hero-cta-find"
                      className="border border-white/40 hover:bg-white/10 text-white rounded-full px-5 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                      <Compass size={14} /> Region per PLZ finden
                    </Link>
                  </div>
                </div>
                <div className="flex flex-col items-start md:items-end gap-3">
                  <HeroTicker frames={TICKER_FRAMES} />
                  <div className="flex gap-4 text-white">
                    <div>
                      <div className="font-mono text-3xl md:text-4xl tabular-nums" style={{ color: "#C8E6C9" }}>
                        <CountUp value={stats.regions} />
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-wider opacity-80">Regionen</div>
                    </div>
                    <div>
                      <div className="font-mono text-3xl md:text-4xl tabular-nums" style={{ color: "#C8E6C9" }}>
                        <CountUp value={stats.days} suffix="T" />
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-wider opacity-80">Verlauf</div>
                    </div>
                    <div>
                      <div className="font-mono text-3xl md:text-4xl tabular-nums" style={{ color: "#C8E6C9" }}>
                        100%
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-wider opacity-80">Gratis</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spotlight Strip — overlaps the hero bottom */}
      <SpotlightStrip data={spotlight} />

      <main className="max-w-6xl mx-auto px-6 py-16 space-y-16">
        {/* 60-second elevator pitch — newcomers see this first after hero */}
        <HowItWorks />

        {/* Season block */}
        <SeasonBlock season={season} />

        {/* Discovery */}
        <DiscoveryGrid />

        {/* Who is this for — 5 personas with curated entry-points */}
        <WhoIsItFor />

        {/* Story telling */}
        <section data-testid="stories-section" className="space-y-10">
          <div>
            <div className="eid-label">Im Augenblick gerade</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tighter mt-1 max-w-3xl">
              Drei Geschichten, live aus den Daten.
            </h2>
          </div>
          {STORIES.map((s, i) => (
            <StoryRow key={i} story={s} index={i} />
          ))}
        </section>

        {/* Better explanations */}
        <ExplainGrid />

        {/* Trust + numbers */}
        <TrustFooter />
      </main>

      <footer className="border-t border-white/10 bg-[#121212] mt-8">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col gap-3 text-xs text-zinc-500 font-mono">
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <span>Bodenblick · © {new Date().getFullYear()} Johanna Maislinger · bodenblick.com</span>
            <span>Daten: Copernicus CDSE · Open-Meteo · Gemini 3 Flash</span>
          </div>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-white/10">
            <Link to="/impressum" className="hover:text-white" data-testid="landing-footer-impressum">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white" data-testid="landing-footer-datenschutz">Datenschutz</Link>
            <Link to="/agb" className="hover:text-white" data-testid="landing-footer-agb">Nutzungsbedingungen</Link>
            <Link to="/embed" className="hover:text-white" data-testid="landing-footer-embed">Widgets einbetten</Link>
            <Link to="/schulgaerten" className="hover:text-white" data-testid="landing-footer-schulgaerten">Schulgärten</Link>
            <Link to="/feldfruechte" className="hover:text-white" data-testid="landing-footer-feldfruechte">Wie erkennt man Feldfrüchte?</Link>
            <Link to="/glossar" className="hover:text-white" data-testid="landing-footer-glossar">Glossar</Link>
            <Link to={`/wrapped/${new Date().getFullYear() - 1}`} className="hover:text-white" data-testid="landing-footer-wrapped">Wrapped {new Date().getFullYear() - 1}</Link>
            <a href="/api/rss/daily.xml" className="hover:text-white" data-testid="landing-footer-rss" target="_blank" rel="noopener noreferrer">RSS</a>
            <ReopenCookieLink />
            <a href="mailto:vision@bodenblick.com" className="hover:text-white ml-auto">vision@bodenblick.com</a>
          </div>
        </div>
      </footer>
      <MobilePulseBar pulse={pulse} />
    </div>
  );
}
